import { api } from "./api";

let scriptPromise = null;

export function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => { scriptPromise = null; reject(new Error("Failed to load Razorpay")); };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Full checkout flow: create order server-side → open Razorpay modal →
 * verify signature server-side. Resolves with the verify response on
 * success; rejects on failure/dismiss.
 */
export async function payWithRazorpay({ plan_id, billing_cycle, service_name, amount_inr, recharge_amount_inr }) {
  await loadRazorpayScript();
  const { data: order } = await api.post("/payments/razorpay/create-order", {
    plan_id, billing_cycle, service_name, amount_inr, recharge_amount_inr,
  });
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "EcomCatalog AI",
      description: order.label,
      order_id: order.order_id,
      prefill: order.prefill,
      theme: { color: "#FF3F6C" },
      modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/razorpay/verify", {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve(data);
        } catch (e) {
          reject(e);
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}

/**
 * Auto-Pay enrolment: create a Razorpay Subscription server-side → open
 * checkout with subscription_id (customer authorizes the monthly mandate,
 * UPI Autopay / card e-mandate) → verify server-side. After this, ₹999 is
 * deducted automatically every month.
 */
export async function enableAutoPay() {
  await loadRazorpayScript();
  const { data: sub } = await api.post("/payments/razorpay/create-subscription", {});
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: sub.key_id,
      subscription_id: sub.subscription_id,
      name: "EcomCatalog AI",
      description: sub.label,
      prefill: sub.prefill,
      theme: { color: "#FF3F6C" },
      modal: { ondismiss: () => reject(new Error("Auto-Pay setup cancelled")) },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/razorpay/verify-subscription", {
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_subscription_id: res.razorpay_subscription_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve(data);
        } catch (e) {
          reject(e);
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp?.error?.description || "Auto-Pay authorization failed"));
    });
    rzp.open();
  });
}
